import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { API } from '@/App';
import WebsiteLayout from '@/components/layout/WebsiteLayout';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useChatbot } from '@/context/ChatbotContext';
import {
  Search,
  Filter,
  User,
  Briefcase,
  Award,
  CheckCircle2,
  Clock,
  ArrowRight,
  Star,
  Code,
  Database,
  Cloud,
  Shield,
  Cpu,
  Layers,
  Users,
  MessageCircle,
  ChevronDown,
  X,
  UserPlus
} from 'lucide-react';

const categoryLabels = {
  ai_integration_engineer: 'AI Integration Engineer',
  ai_application_developer: 'AI Application Developer',
  ai_automation_engineer: 'AI Automation Engineer',
  prompt_engineer: 'Prompt Engineer',
  ai_workflow_designer: 'AI Workflow Designer',
  ai_qa_engineer: 'AI QA Engineer',
  ai_validation_engineer: 'AI Validation Engineer',
  ai_data_engineer: 'AI Data Engineer',
  ai_rag_engineer: 'AI RAG Engineer',
  ai_devops_engineer: 'AI DevOps Engineer',
  mlops_engineer: 'MLOps Engineer',
  ai_platform_engineer: 'AI Platform Engineer',
  ai_solutions_engineer: 'AI Solutions Engineer',
  ai_support_engineer: 'AI Support Engineer',
  ai_chatbot_engineer: 'AI Chatbot Engineer',
  ai_copilot_engineer: 'AI Copilot Engineer',
  ai_api_engineer: 'AI API Engineer',
  ai_security_engineer: 'AI Security Engineer',
  ai_governance_engineer: 'AI Governance Engineer',
  ai_compliance_analyst: 'AI Compliance Analyst',
  ai_risk_analyst: 'AI Risk Analyst',
  ai_model_monitoring_engineer: 'AI Model Monitoring Engineer',
  ai_performance_engineer: 'AI Performance Engineer',
  ai_business_analyst: 'AI Business Analyst',
  ai_product_engineer: 'AI Product Engineer',
  ai_implementation_consultant: 'AI Implementation Consultant',
  ai_migration_engineer: 'AI Migration Engineer',
  ai_knowledge_systems_engineer: 'AI Knowledge Systems Engineer',
};

const categoryIcons = {
  ai_integration_engineer: Cpu,
  ai_application_developer: Code,
  ai_automation_engineer: Layers,
  prompt_engineer: MessageCircle,
  ai_workflow_designer: Layers,
  ai_qa_engineer: Shield,
  ai_validation_engineer: Shield,
  ai_data_engineer: Database,
  ai_rag_engineer: Database,
  ai_devops_engineer: Cloud,
  mlops_engineer: Layers,
  ai_platform_engineer: Cloud,
  ai_solutions_engineer: Cpu,
  ai_support_engineer: Users,
  ai_chatbot_engineer: MessageCircle,
  ai_copilot_engineer: Cpu,
  ai_api_engineer: Code,
  ai_security_engineer: Shield,
  ai_governance_engineer: Shield,
  ai_compliance_analyst: Shield,
  ai_risk_analyst: Shield,
  ai_model_monitoring_engineer: Cpu,
  ai_performance_engineer: Cpu,
  ai_business_analyst: Users,
  ai_product_engineer: Cpu,
  ai_implementation_consultant: Users,
  ai_migration_engineer: Cloud,
  ai_knowledge_systems_engineer: Database,
};

const expertiseLevelLabels = {
  junior: 'Junior',
  mid: 'Mid-Level',
  senior: 'Senior',
  lead: 'Lead',
  principal: 'Principal'
};

const availabilityConfig = {
  available: { label: 'Available Now', color: 'bg-green-100 text-green-700 border-green-200' },
  engaged: { label: 'Currently Engaged', color: 'bg-yellow-100 text-yellow-700 border-yellow-200' },
  coming_soon: { label: 'Available Soon', color: 'bg-blue-100 text-blue-700 border-blue-200' }
};

export default function TalentPoolPage() {
  const [talents, setTalents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedAvailability, setSelectedAvailability] = useState('all');
  const [selectedExpertise, setSelectedExpertise] = useState('all');
  const [expandedTalent, setExpandedTalent] = useState(null);
  const { openChatbot } = useChatbot();

  useEffect(() => {
    fetchTalents();
  }, []);

  const fetchTalents = async () => {
    try {
      const response = await axios.get(`${API}/talents`);
      setTalents(response.data);
    } catch (error) {
      console.error('Error fetching talents:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredTalents = talents.filter(talent => {
    const matchesSearch = searchTerm === '' || 
      talent.display_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      talent.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      talent.skills.some(skill => skill.toLowerCase().includes(searchTerm.toLowerCase()));
    
    const matchesCategory = selectedCategory === 'all' || talent.category === selectedCategory;
    const matchesAvailability = selectedAvailability === 'all' || talent.availability === selectedAvailability;
    const matchesExpertise = selectedExpertise === 'all' || talent.expertise_level === selectedExpertise;
    
    return matchesSearch && matchesCategory && matchesAvailability && matchesExpertise;
  });

  const featuredTalents = filteredTalents.filter(t => t.is_featured);
  const otherTalents = filteredTalents.filter(t => !t.is_featured);

  const handleHireClick = (talentName) => {
    openChatbot('chat');
  };

  return (
    <WebsiteLayout>
      {/* Hero Section */}
      <section className="pt-32 pb-16 bg-gradient-to-br from-slate-900 via-blue-950 to-indigo-950 relative overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-1/4 left-1/4 w-[400px] h-[400px] bg-blue-500/20 rounded-full blur-[120px]"></div>
          <div className="absolute bottom-1/4 right-1/4 w-[400px] h-[400px] bg-purple-500/20 rounded-full blur-[120px]"></div>
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Badge className="mb-4 bg-blue-500/20 text-blue-300 border-blue-500/30">
            AI-Powered Teams
          </Badge>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6">
            AI Talent Pool
          </h1>
          <p className="text-xl text-slate-300 max-w-3xl mx-auto mb-8">
            Access our network of pre-vetted AI engineers, data specialists, and ML experts ready to accelerate your projects.
          </p>
          
          {/* Stats */}
          <div className="flex flex-wrap justify-center gap-8 mt-12">
            <div className="text-center">
              <p className="text-4xl font-bold text-white">{talents.length}+</p>
              <p className="text-sm text-slate-400">AI Professionals</p>
            </div>
            <div className="text-center">
              <p className="text-4xl font-bold text-white">{talents.filter(t => t.availability === 'available').length}</p>
              <p className="text-sm text-slate-400">Available Now</p>
            </div>
            <div className="text-center">
              <p className="text-4xl font-bold text-white">50+</p>
              <p className="text-sm text-slate-400">Skills Covered</p>
            </div>
          </div>

          {/* Join CTA */}
          <div className="mt-10">
            <Link to="/join-talent-pool">
              <Button size="lg" className="bg-white text-slate-900 hover:bg-slate-100">
                <UserPlus className="w-5 h-5 mr-2" />
                Join Our Talent Pool
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Filters Section */}
      <section className="py-8 bg-slate-800 border-b border-slate-700 sticky top-20 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col lg:flex-row gap-4 items-center">
            {/* Search */}
            <div className="relative flex-1 w-full lg:max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
              <Input
                placeholder="Search by name, title, or skills..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 h-12 bg-slate-900 border-slate-600 text-white placeholder:text-slate-500"
              />
            </div>
            
            {/* Filters */}
            <div className="flex flex-wrap gap-3 items-center">
              <Select value={selectedCategory} onValueChange={setSelectedCategory}>
                <SelectTrigger className="w-[180px] h-12 bg-slate-900 border-slate-600 text-white">
                  <SelectValue placeholder="Category" />
                </SelectTrigger>
                <SelectContent className="bg-slate-800 border-slate-700">
                  <SelectItem value="all" className="text-white hover:bg-slate-700">All Categories</SelectItem>
                  {Object.entries(categoryLabels).map(([key, label]) => (
                    <SelectItem key={key} value={key} className="text-white hover:bg-slate-700">{label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              
              <Select value={selectedAvailability} onValueChange={setSelectedAvailability}>
                <SelectTrigger className="w-[180px] h-12 bg-slate-900 border-slate-600 text-white">
                  <SelectValue placeholder="Availability" />
                </SelectTrigger>
                <SelectContent className="bg-slate-800 border-slate-700">
                  <SelectItem value="all" className="text-white hover:bg-slate-700">All Availability</SelectItem>
                  <SelectItem value="available" className="text-white hover:bg-slate-700">Available Now</SelectItem>
                  <SelectItem value="engaged" className="text-white hover:bg-slate-700">Currently Engaged</SelectItem>
                  <SelectItem value="coming_soon" className="text-white hover:bg-slate-700">Available Soon</SelectItem>
                </SelectContent>
              </Select>
              
              <Select value={selectedExpertise} onValueChange={setSelectedExpertise}>
                <SelectTrigger className="w-[160px] h-12 bg-slate-900 border-slate-600 text-white">
                  <SelectValue placeholder="Expertise" />
                </SelectTrigger>
                <SelectContent className="bg-slate-800 border-slate-700">
                  <SelectItem value="all" className="text-white hover:bg-slate-700">All Levels</SelectItem>
                  {Object.entries(expertiseLevelLabels).map(([key, label]) => (
                    <SelectItem key={key} value={key} className="text-white hover:bg-slate-700">{label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              
              {(searchTerm || selectedCategory !== 'all' || selectedAvailability !== 'all' || selectedExpertise !== 'all') && (
                <Button 
                  variant="ghost" 
                  size="sm"
                  onClick={() => {
                    setSearchTerm('');
                    setSelectedCategory('all');
                    setSelectedAvailability('all');
                    setSelectedExpertise('all');
                  }}
                  className="text-slate-400 hover:text-white"
                >
                  <X className="w-4 h-4 mr-1" />
                  Clear
                </Button>
              )}
            </div>
          </div>
          
          <p className="text-sm text-slate-400 mt-4">
            Showing {filteredTalents.length} of {talents.length} professionals
          </p>
        </div>
      </section>

      {/* Talent Grid */}
      <section className="py-16 bg-slate-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {loading ? (
            <div className="flex justify-center items-center h-64">
              <div className="w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
            </div>
          ) : filteredTalents.length === 0 ? (
            <div className="text-center py-16">
              <Users className="w-16 h-16 text-slate-600 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-white mb-2">No matches found</h3>
              <p className="text-slate-400">Try adjusting your filters or search term</p>
            </div>
          ) : (
            <>
              {/* Featured Talents */}
              {featuredTalents.length > 0 && (
                <div className="mb-12">
                  <h2 className="text-2xl font-bold text-white mb-6 flex items-center gap-2">
                    <Star className="w-6 h-6 text-yellow-500" />
                    Featured Professionals
                  </h2>
                  <div className="grid md:grid-cols-2 gap-6">
                    {featuredTalents.map((talent) => (
                      <TalentCard 
                        key={talent.id} 
                        talent={talent} 
                        expanded={expandedTalent === talent.id}
                        onToggle={() => setExpandedTalent(expandedTalent === talent.id ? null : talent.id)}
                        onHire={() => handleHireClick(talent.display_name)}
                        featured
                      />
                    ))}
                  </div>
                </div>
              )}
              
              {/* Other Talents */}
              {otherTalents.length > 0 && (
                <div>
                  <h2 className="text-2xl font-bold text-white mb-6">
                    All Professionals
                  </h2>
                  <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {otherTalents.map((talent) => (
                      <TalentCard 
                        key={talent.id} 
                        talent={talent} 
                        expanded={expandedTalent === talent.id}
                        onToggle={() => setExpandedTalent(expandedTalent === talent.id ? null : talent.id)}
                        onHire={() => handleHireClick(talent.display_name)}
                      />
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </section>

      {/* Join Talent Pool Section */}
      <section className="py-16 bg-slate-800 border-t border-slate-700">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Badge className="mb-4 bg-green-500/20 text-green-400 border-green-500/30">
            For AI Professionals
          </Badge>
          <h2 className="text-3xl font-bold text-white mb-4">
            Are You an AI Professional?
          </h2>
          <p className="text-lg text-slate-400 mb-8">
            Join our curated talent network and get connected with top companies looking for specialized AI expertise.
          </p>
          <div className="flex flex-wrap justify-center gap-6 mb-8">
            <div className="flex items-center gap-2 text-slate-300">
              <CheckCircle2 className="w-5 h-5 text-green-500" />
              <span>Verified opportunities</span>
            </div>
            <div className="flex items-center gap-2 text-slate-300">
              <CheckCircle2 className="w-5 h-5 text-green-500" />
              <span>Competitive rates</span>
            </div>
            <div className="flex items-center gap-2 text-slate-300">
              <CheckCircle2 className="w-5 h-5 text-green-500" />
              <span>Flexible engagements</span>
            </div>
          </div>
          <Link to="/join-talent-pool">
            <Button size="lg" className="bg-green-600 hover:bg-green-700 text-white">
              <UserPlus className="w-5 h-5 mr-2" />
              Submit Your Profile
            </Button>
          </Link>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-blue-600 to-indigo-600">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
            Need Custom AI Team Requirements?
          </h2>
          <p className="text-xl text-blue-100 mb-8">
            Let us know your specific needs and we&apos;ll match you with the perfect AI professionals.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              size="lg" 
              variant="secondary" 
              className="text-blue-600"
              onClick={() => openChatbot('booking')}
            >
              Schedule Consultation
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
            <Button 
              size="lg" 
              variant="outline" 
              className="text-white border-white hover:bg-white/10"
              onClick={() => openChatbot('chat')}
            >
              <MessageCircle className="w-5 h-5 mr-2" />
              Chat with Sales
            </Button>
          </div>
        </div>
      </section>
    </WebsiteLayout>
  );
}

// Talent Card Component
function TalentCard({ talent, expanded, onToggle, onHire, featured = false }) {
  const CategoryIcon = categoryIcons[talent.category] || Cpu;
  const availability = availabilityConfig[talent.availability] || availabilityConfig.available;
  const { openChatbot } = useChatbot();
  
  return (
    <Card className={`border border-slate-700 shadow-lg hover:shadow-xl hover:shadow-blue-500/10 transition-all duration-300 bg-slate-800 overflow-hidden ${featured ? 'ring-2 ring-yellow-400/50' : ''}`}>
      <CardContent className="p-6">
        {/* Header */}
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-white font-bold text-lg">
              {talent.display_name.charAt(0)}
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h3 className="text-lg font-bold text-white">{talent.display_name}</h3>
                {featured && <Star className="w-4 h-4 text-yellow-500 fill-yellow-500" />}
              </div>
              <p className="text-sm text-blue-400 font-medium">{talent.title}</p>
            </div>
          </div>
          <Badge className={`${availability.color} text-xs`}>
            {availability.label}
          </Badge>
        </div>
        
        {/* Bio */}
        <p className="text-sm text-slate-400 mb-4 line-clamp-2">{talent.bio}</p>
        
        {/* Quick Stats */}
        <div className="flex flex-wrap gap-4 mb-4 text-sm">
          <div className="flex items-center gap-1 text-slate-400">
            <Briefcase className="w-4 h-4" />
            <span>{talent.years_of_experience} years</span>
          </div>
          <div className="flex items-center gap-1 text-slate-400">
            <Award className="w-4 h-4" />
            <span className="capitalize">{expertiseLevelLabels[talent.expertise_level] || talent.expertise_level}</span>
          </div>
          <div className="flex items-center gap-1 text-slate-400">
            <CategoryIcon className="w-4 h-4" />
            <span>{categoryLabels[talent.category] || talent.category}</span>
          </div>
        </div>
        
        {/* Top Skills */}
        <div className="flex flex-wrap gap-2 mb-4">
          {talent.skills.slice(0, 5).map((skill, idx) => (
            <Badge key={idx} variant="outline" className="text-xs bg-slate-700/50 border-slate-600 text-slate-300">
              {skill}
            </Badge>
          ))}
          {talent.skills.length > 5 && (
            <Badge variant="outline" className="text-xs bg-slate-700/50 border-slate-600 text-slate-400">
              +{talent.skills.length - 5} more
            </Badge>
          )}
        </div>
        
        {/* Expand/Collapse Button */}
        <button 
          onClick={onToggle}
          className="flex items-center gap-1 text-sm text-blue-400 hover:text-blue-300 mb-4"
        >
          <ChevronDown className={`w-4 h-4 transition-transform ${expanded ? 'rotate-180' : ''}`} />
          {expanded ? 'Show Less' : 'View Details'}
        </button>
        
        {/* Expanded Details */}
        {expanded && (
          <div className="border-t border-slate-700 pt-4 mt-4 space-y-4 animate-fade-in">
            {/* Industries */}
            {talent.industries.length > 0 && (
              <div>
                <h4 className="text-sm font-semibold text-white mb-2">Industries</h4>
                <div className="flex flex-wrap gap-2">
                  {talent.industries.map((industry, idx) => (
                    <Badge key={idx} className="bg-blue-500/20 text-blue-300 text-xs">
                      {industry}
                    </Badge>
                  ))}
                </div>
              </div>
            )}
            
            {/* Certifications */}
            {talent.certifications.length > 0 && (
              <div>
                <h4 className="text-sm font-semibold text-white mb-2">Certifications</h4>
                <div className="space-y-1">
                  {talent.certifications.map((cert, idx) => (
                    <div key={idx} className="flex items-center gap-2 text-sm text-slate-400">
                      <CheckCircle2 className="w-4 h-4 text-green-500" />
                      {cert}
                    </div>
                  ))}
                </div>
              </div>
            )}}
            
            {/* Previous Projects */}
            {talent.previous_projects.length > 0 && (
              <div>
                <h4 className="text-sm font-semibold text-slate-900 dark:text-white mb-2">Notable Projects</h4>
                <div className="space-y-3">
                  {talent.previous_projects.map((project, idx) => (
                    <div key={idx} className="bg-slate-50 dark:bg-slate-800 rounded-lg p-3">
                      <p className="font-medium text-slate-900 dark:text-white text-sm">{project.name}</p>
                      <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">{project.description}</p>
                      <div className="flex items-center gap-2 mt-2 text-xs text-slate-500">
                        <Badge variant="outline" className="text-xs">{project.industry}</Badge>
                        <span>• {project.role}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
        
        {/* Action Buttons */}
        <div className="flex gap-2 mt-4">
          <Button 
            className="flex-1 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700"
            onClick={() => openChatbot('chat')}
          >
            <MessageCircle className="w-4 h-4 mr-2" />
            Inquire
          </Button>
          <Button 
            variant="outline" 
            className="flex-1"
            onClick={() => openChatbot('booking')}
          >
            Schedule Call
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
